import type { FC } from "react";
import { TrendingUp, TrendingDown, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

type StatisticsItem = {
  title: string;
  value?: number | string | null;
  percentage?: number | null;
  tooltip?: string;
};

type StatisticsProps = {
  statisticsConfig: StatisticsItem[];
  isLoading?: boolean;
  isError?: boolean;
};

const Statistics: FC<StatisticsProps> = ({
  statisticsConfig,
  isLoading,
  isError,
}) => {
  return (
    <div className="grid gap-4 p-4 bg-card rounded-lg shadow-md grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border">
      {statisticsConfig.map((item) => (
        <div
          key={item.title}
          className="flex flex-col p-4 bg-background rounded-lg shadow-xs border"
        >
          <div className="text-md font-medium flex items-center gap-2">
            {item.title}
            {item.tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="size-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{item.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="text-2xl font-bold mt-2">
            {isLoading ? (
              <Spinner />
            ) : item.value != null && !isLoading && !isError ? (
              item.value
            ) : isError ? (
              <span className="text-destructive">Fejl</span>
            ) : (
              "-"
            )}
          </div>
          <div className="flex flex-row items-center gap-2 mt-2">
            {isLoading ? (
              <span className="animate-pulse">...</span>
            ) : isError ? (
              <span className="text-destructive">-</span>
            ) : item.percentage !== undefined && item.percentage !== null ? (
              <>
                {item.percentage >= 0 ? (
                  <TrendingUp className="size-4 text-success" />
                ) : (
                  <TrendingDown className="size-4 text-destructive" />
                )}
                <div
                  className={cn(
                    "text-sm font-medium",
                    item.percentage >= 0 ? "text-success" : "text-destructive"
                  )}
                >
                  {item.percentage.toFixed(1)}%
                </div>
              </>
            ) : (
              "-"
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Statistics;
